import { useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Box, Button, Stack, Paper, toast, Typography, Tooltip } from '@evaneos/b2b-ui';
import cx from 'classnames';

import TextFormatIcon from '@evaneos/b2b-ui/icons/TextFormat';

import {
    useEditMessageMutation,
    useEditScheduledMessageMutation,
} from '../../../../../resources/discussion/api';
import { DossierId } from '../../../../../resources/dossier/types';
import {
    Message,
    MessageAttachment,
    ScheduledMessage,
} from '../../../../../resources/discussion/types';
import { useAppDispatch, useAppSelector } from '../../../../../shared/redux';
import {
    fetchScheduledMessagesRequest,
    getMessageRequest,
    updateMessageFailure,
    updateMessageRequest,
} from '../../../../../resources/discussion';
import { updateMessageStatus } from '../../../../../resources/discussion/selectors';
import { PENDING } from '../../../../../design-system/constants';
import {
    useGetTrackedTripProperties,
    useTrackEventAmplitude,
} from '../../../../../shared/analytics';
import { useHeightObserver } from '../../../../../shared/hooks/useHeightObserver';
import {
    EditorContent,
    QuillEditor,
} from '../../../../../shared/components/QuillEditor/QuillEditor';
import { toSafeHtml } from '../../utils/messageHtmlConverter';

import { AttachmentViewAndDelete } from './AttachmentViewAndDelete';
import s from './NewMessageBox.css';
import { tooltipSlotProps } from './NewMessageBox';

type EditMessageBoxProps = {
    dossierId: DossierId;
    editingMessage: Message | ScheduledMessage;
    onCancel: () => void;
    onHeightChange: () => void;
};

export const EditMessageBox = ({
    dossierId,
    editingMessage,
    onCancel,
    onHeightChange,
}: EditMessageBoxProps) => {
    const dispatch = useAppDispatch();
    const { formatMessage } = useIntl();
    const containerRef = useHeightObserver<HTMLElement>(onHeightChange);
    const { mutate: editMessage } = useEditMessageMutation();
    const { mutate: editScheduledMessage } = useEditScheduledMessageMutation();
    const status = useAppSelector(updateMessageStatus(dossierId));
    const { trackEvent } = useTrackEventAmplitude();
    const trackedTripProperties = useGetTrackedTripProperties(dossierId);
    const [showToolbar, setShowToolbar] = useState<boolean>(true);

    const { id, attachments: initialAttachments } = editingMessage;
    const isScheduledMessage = 'scheduledDate' in editingMessage;
    const initialValue = isScheduledMessage ? editingMessage.content : editingMessage.body;
    const initialValueEnrichedRef = useRef<string | null>(null);

    const [editorContent, setEditorContent] = useState<EditorContent>({
        text: initialValue,
        html: toSafeHtml(isScheduledMessage ? editingMessage.contentHtml : editingMessage.htmlBody),
    });
    const [attachments, setAttachments] = useState<MessageAttachment[]>(initialAttachments ?? []);

    const messageId = parseInt(id.toString());
    const isDirty =
        (editorContent.text.trim() !== '' &&
            editorContent.html !== initialValueEnrichedRef.current) ||
        attachments.length !== (initialAttachments?.length ?? 0);

    const handleDeleteAttachment = (deletedAttachment: MessageAttachment) => {
        setAttachments((cur) => cur.filter(({ id }) => id !== deletedAttachment.id));
    };

    const handleChangeEnrichText = (content: EditorContent) => {
        // Capture the normalized HTML from Quill on first change
        if (initialValueEnrichedRef.current === null) {
            initialValueEnrichedRef.current = content.html;
        }
        setEditorContent(content);
    };

    const submitEditMessage = () => {
        const content = editorContent.text;
        const contentHtml = editorContent.html;
        dispatch(updateMessageRequest(messageId, dossierId, content, contentHtml, false));
        editMessage(
            {
                dossierId,
                messageId,
                content,
                contentHtml,
                attachmentsIds: attachments
                    .filter(({ id }) => id != undefined)
                    .map(({ id }) => id as number),
            },
            {
                onSuccess: () => {
                    dispatch(getMessageRequest(messageId, dossierId));
                },
                onError: (error) => {
                    toast.error(
                        error.body?.error.includes('Message cannot be modified after 70 minutes')
                            ? formatMessage({
                                  id: 'discussion.edit_message.error.expired_edition',
                              })
                            : formatMessage({ id: 'global.generic_error' }),
                    );
                    dispatch(updateMessageFailure(messageId, dossierId, error.message));
                },
            },
        );
    };

    const submitEditScheduledMessage = () => {
        const content = editorContent.text;
        const contentHtml = editorContent.html;
        dispatch(updateMessageRequest(Number(id), dossierId, content, contentHtml, true));
        editScheduledMessage(
            {
                dossierId,
                scheduledMessageId: id,
                content,
                contentHtml,
                attachmentsIds: attachments.map(({ id }) => id as number),
            },
            {
                onSuccess: () => {
                    dispatch(fetchScheduledMessagesRequest(dossierId));
                },
                onError: (error) => {
                    toast.error(formatMessage({ id: 'global.generic_error' }));
                    dispatch(updateMessageFailure(messageId, dossierId, error.message));
                },
            },
        );
    };

    const handleSubmit = () => {
        if (isScheduledMessage) {
            submitEditScheduledMessage();
        } else {
            submitEditMessage();
        }
        trackEvent((ampli, defaultProperties) =>
            ampli.messageEditSubmitClicked({ ...defaultProperties, ...trackedTripProperties }),
        );
    };

    const handleCancel = () => {
        onCancel();
        trackEvent((ampli, defaultProperties) =>
            ampli.messageEditCancelClicked({ ...defaultProperties, ...trackedTripProperties }),
        );
    };

    return (
        <Box
            ref={containerRef}
            sx={{
                width: '100%',
                p: {
                    xs: '16px 16px 28px 16px',
                    lg: '16px 24px 28px 24px',
                },
                borderTop: '1px solid',
                borderColor: 'border.main',
            }}
        >
            <Typography variant="h4" mb={1}>
                {formatMessage({ id: 'discussion.edit_message.button' })}
            </Typography>

            <Paper
                variant="outlined"
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    borderColor: 'border.main',
                }}
            >
                <QuillEditor
                    value={toSafeHtml(editorContent.html)}
                    onTextChange={handleChangeEnrichText}
                    autoFocus
                    className={cx('quill-editor-container', s.enrichTextarea)}
                    readOnly={status === PENDING}
                    placeholder={formatMessage({
                        id: 'discussion.input_placeholder',
                    })}
                    showToolbar={showToolbar}
                />

                {attachments.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {Object.values(attachments).map((attachment) => (
                            <AttachmentViewAndDelete
                                key={attachment.id}
                                attachment={attachment}
                                onDeleteAttachment={handleDeleteAttachment}
                            />
                        ))}
                    </Box>
                )}

                <Stack direction="row" justifyContent="space-between" padding={1.5}>
                    <Tooltip
                        title={formatMessage({
                            id: showToolbar
                                ? 'discussion.editor.hide_format_toolbar'
                                : 'discussion.editor.show_format_toolbar',
                        })}
                        placement="top"
                        disableInteractive
                        slotProps={tooltipSlotProps}
                    >
                        <Button
                            onClick={() => setShowToolbar(!showToolbar)}
                            variant="text"
                            color="secondary"
                            sx={{ width: '40px', minWidth: '40px' }}
                            aria-label={formatMessage({
                                id: showToolbar
                                    ? 'discussion.editor.hide_format_toolbar'
                                    : 'discussion.editor.show_format_toolbar',
                            })}
                        >
                            <TextFormatIcon />
                        </Button>
                    </Tooltip>
                    <Stack direction="row" spacing={1}>
                        <Button variant="outlined" onClick={handleCancel}>
                            {formatMessage({ id: 'form.cancel' })}
                        </Button>
                        <Button
                            variant="contained"
                            disabled={!isDirty}
                            loading={status === PENDING}
                            onClick={handleSubmit}
                        >
                            {formatMessage({ id: 'discussion.edit_message.submit' })}
                        </Button>
                    </Stack>
                </Stack>
            </Paper>
        </Box>
    );
};
